import { computed, inject, Injectable, signal } from '@angular/core';
import {
  confirmSignIn,
  confirmUserAttribute,
  fetchAuthSession,
  fetchMFAPreference,
  sendUserAttributeVerificationCode,
  setUpTOTP,
  signIn,
  signInWithRedirect,
  signOut,
  updateMFAPreference,
  updatePassword,
  updateUserAttribute,
  verifyTOTPSetup,
} from 'aws-amplify/auth';
import { defer, firstValueFrom, from, Observable, tap } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { OnboardingService, ProfileService } from '@common/services';
import { LoaderKey } from '../enums';
import type {
  AuthUser,
  LoginResult,
  MfaChallengeStep,
  MfaStatus,
  OnboardingDetails,
  SsoProvider,
  TotpSetup,
} from '../types';
import { LoadingService } from './loading.service';

type SignInOutput = Awaited<ReturnType<typeof signIn>>;

const MFA_STEPS: ReadonlySet<string> = new Set<MfaChallengeStep>([
  'CONFIRM_SIGN_IN_WITH_SMS_CODE',
  'CONFIRM_SIGN_IN_WITH_TOTP_CODE',
  'CONFIRM_SIGN_IN_WITH_EMAIL_CODE',
]);

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly onboarding = inject(OnboardingService);
  private readonly profile = inject(ProfileService);
  private readonly loading = inject(LoadingService);

  private readonly _isAuthenticated = signal(false);
  private readonly _userData = signal<AuthUser | null>(null);
  private readonly _firstLogin = signal(false);

  readonly isAuthenticated = this._isAuthenticated.asReadonly();
  readonly userData = this._userData.asReadonly();

  readonly emailVerified = computed(() => this._userData()?.email_verified === true);

  readonly displayName = computed(() => {
    const profile = this.profile.profile();
    const name = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim();
    if (name) return name;
    return this._userData()?.email ?? '';
  });

  readonly onboardingRequired = computed(() => {
    if (!this._isAuthenticated()) return false;
    if (this._firstLogin()) return true;
    const profile = this.profile.profile();
    if (!profile) return false;
    return !profile.firstName?.trim() || !profile.lastName?.trim();
  });

  private profileReady: Promise<void> | null = null;

  async initialize(): Promise<void> {
    try {
      await this.loadUserData();
    } catch {
      this._isAuthenticated.set(false);
    }
  }

  ensureProfileLoaded(): Promise<void> {
    return (this.profileReady ??= this.loadProfile());
  }

  getIdToken(): Observable<string> {
    return from(fetchAuthSession()).pipe(
      map((session) => session.tokens?.idToken?.toString() ?? ''),
    );
  }

  signInWithCredentials(username: string, password: string): Observable<LoginResult> {
    this._firstLogin.set(false);
    return from(
      signIn({
        username,
        password,
        options: { authFlowType: 'USER_PASSWORD_AUTH' },
      }),
    ).pipe(switchMap((output) => this.resolveStep(output)));
  }

  completeNewPassword(newPassword: string): Observable<LoginResult> {
    this._firstLogin.set(true);
    return from(confirmSignIn({ challengeResponse: newPassword })).pipe(
      switchMap((output) => this.resolveStep(output)),
    );
  }

  respondToMfaChallenge(code: string): Observable<LoginResult> {
    return from(confirmSignIn({ challengeResponse: code })).pipe(
      switchMap((output) => this.resolveStep(output)),
    );
  }

  sendEmailVerificationCode(): Observable<void> {
    return from(sendUserAttributeVerificationCode({ userAttributeKey: 'email' })).pipe(
      map(() => undefined),
    );
  }

  confirmEmail(code: string): Observable<void> {
    return from(confirmUserAttribute({ userAttributeKey: 'email', confirmationCode: code })).pipe(
      switchMap(() => this.refreshSession()),
    );
  }

  changePassword(oldPassword: string, newPassword: string): Observable<void> {
    return from(updatePassword({ oldPassword, newPassword })).pipe(map(() => undefined));
  }

  /**
   * Starts an email change. Cognito keeps the old address until the code sent to the new one is
   * confirmed through `confirmEmail`, so a typo cannot lock anyone out.
   */
  requestEmailChange(email: string): Observable<void> {
    return from(
      updateUserAttribute({ userAttribute: { attributeKey: 'email', value: email } }),
    ).pipe(map(() => undefined));
  }

  mfaStatus(): Observable<MfaStatus> {
    return from(fetchMFAPreference()).pipe(
      map((output) => ({
        enabled: output.enabled ?? [],
        preferred: output.preferred ?? null,
      })),
    );
  }

  beginTotpSetup(accountName: string): Observable<TotpSetup> {
    return from(setUpTOTP()).pipe(
      map((details) => ({
        secret: details.sharedSecret,
        uri: details.getSetupUri('GuestRevu', accountName).toString(),
      })),
    );
  }

  confirmTotpSetup(code: string): Observable<void> {
    return from(verifyTOTPSetup({ code })).pipe(
      switchMap(() => from(updateMFAPreference({ totp: 'PREFERRED' }))),
      map(() => undefined),
    );
  }

  disableTotp(): Observable<void> {
    return from(updateMFAPreference({ totp: 'DISABLED' })).pipe(map(() => undefined));
  }

  completeOnboarding(details: OnboardingDetails): Observable<void> {
    return this.onboarding.complete(details).pipe(
      switchMap(async () => {
        this._firstLogin.set(false);
        await this.refreshProfile();
      }),
    );
  }

  signInWithProvider(provider: SsoProvider): Observable<void> {
    if (provider === 'Microsoft') {
      return from(signInWithRedirect({ provider: { custom: provider } }));
    }
    return from(signInWithRedirect({ provider, options: {} }));
  }

  signOut(): void {
    this._isAuthenticated.set(false);
    this._userData.set(null);
    this._firstLogin.set(false);
    this.profileReady = null;
    this.profile.clear();
    signOut({
      global: true,
      oauth: {
        redirectUrl: 'https://' + window.location.host + '/auth/login',
      },
    }).catch(() => {});
  }

  /** Keep the session available for retry if remote session revocation fails. */
  signOutEverywhere(): Observable<void> {
    return defer(() => signOut({ global: true })).pipe(
      tap(() => {
        this._isAuthenticated.set(false);
        this._userData.set(null);
        this._firstLogin.set(false);
        this.profileReady = null;
        this.profile.clear();
      }),
    );
  }

  private async resolveStep(output: SignInOutput): Promise<LoginResult> {
    if (output.isSignedIn) {
      await this.finalizeSession();
      return { next: 'done' };
    }

    const step = output.nextStep.signInStep;
    if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return { next: 'newPassword' };
    }
    if (MFA_STEPS.has(step)) {
      return { next: 'mfa', step: step as MfaChallengeStep };
    }
    return { next: 'unsupported', step };
  }

  private async finalizeSession(): Promise<void> {
    await this.loadUserData();
  }

  private refreshProfile(): Promise<void> {
    return (this.profileReady = this.loadProfile());
  }

  private async refreshSession(): Promise<void> {
    await fetchAuthSession({ forceRefresh: true });
    await this.loadUserData();
  }

  private async loadUserData(): Promise<void> {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    if (!idToken) throw new Error('No session');
    this._userData.set(idToken.payload satisfies AuthUser);
    this._isAuthenticated.set(true);
  }

  private async loadProfile(): Promise<void> {
    this.loading.show(LoaderKey.GLOBAL, { label: 'Loading your workspace…' });
    try {
      await firstValueFrom(this.profile.getProfile());
    } catch {
      this.profile.clear();
    } finally {
      this.loading.hide(LoaderKey.GLOBAL);
    }
  }
}
